import * as React from 'react'
import { useGetAllArticles, useGetAllCategories, useIsLoadingCategoriesOrArticles } from 'products/redux'
import { useGetTimeSlotGroupsQuery } from 'timeSlots/reduxQuery'
import { useGetCapacitiesQuery } from 'capacity/reduxQuery'
import { useTimeSlotGroupDetails } from 'capacity/hooks/useTimeSlotGroupDetails'
import { capacityPaths } from 'capacity/paths'
import { FORM_SECTION } from 'capacity/copy'
import { Reveal } from 'uiComponents/reveal/reveal'
import { CapacitySectionShell } from './capacitySectionShell'
import { CapacityRow, LinesRow, MessageRow, PendingRow } from './capacitySectionRow'
import { TimeSlotGroupCard } from './timeSlotGroupCard'
import { categoryCapacityRows, categoryGroupBlocks, productsLosingTimeSlots } from './sectionModel'

interface CategoryCapacitySectionProps {
    accountSlug: string
    categoryId: string
    timeSlotsEnabled: boolean
    savedTimeSlotsEnabled: boolean
    timeSlotsControl: React.ReactNode
}

export function CategoryCapacitySection({
    accountSlug,
    categoryId,
    timeSlotsEnabled,
    savedTimeSlotsEnabled,
    timeSlotsControl,
}: CategoryCapacitySectionProps) {
    const isNew = categoryId === 'new'
    const articles = useGetAllArticles()
    const categories = useGetAllCategories()
    const articlesLoading = useIsLoadingCategoriesOrArticles()
    const capacities = useGetCapacitiesQuery(accountSlug, { skip: isNew })
    const groupsQuery = useGetTimeSlotGroupsQuery(accountSlug, { skip: isNew })
    const groups = groupsQuery.data?.groups

    const rows = React.useMemo(
        () => (isNew ? [] : categoryCapacityRows(accountSlug, categoryId, articles, capacities.data)),
        [isNew, accountSlug, categoryId, articles, capacities.data],
    )
    const blocks = React.useMemo(
        () => (groups && !isNew ? categoryGroupBlocks(categoryId, groups, categories) : null),
        [groups, isNew, categoryId, categories],
    )
    const neededIds = React.useMemo(
        () =>
            groupsQuery.data && !groupsQuery.data.slotsIncluded && blocks
                ? blocks.flatMap((b) => b.groups.map((g) => g.id))
                : [],
        [groupsQuery.data, blocks],
    )
    const details = useTimeSlotGroupDetails(accountSlug, neededIds)

    const turnedOff = !isNew && savedTimeSlotsEnabled && !timeSlotsEnabled
    const losing = React.useMemo(
        () => (groups && turnedOff ? productsLosingTimeSlots(categoryId, articles, groups, categories) : null),
        [groups, turnedOff, categoryId, articles, categories],
    )
    // The warning opens once the count is known (or the groups failed), so it never rewrites itself.
    const countUnknown = groupsQuery.isError
    const warningOpen = turnedOff && (countUnknown || (losing ?? 0) > 0)

    let capacityContent: React.ReactNode
    if (isNew) {
        capacityContent = <MessageRow text={FORM_SECTION.saveCategoryFirst} />
    } else if (articlesLoading || capacities.isLoading) {
        capacityContent = <PendingRow />
    } else if (rows.length === 0) {
        capacityContent = <MessageRow text={FORM_SECTION.categoryNone} />
    } else {
        capacityContent = rows.map((row) => (
            <CapacityRow key={row.key} href={row.href} name={row.name} detail={row.detail} pending={row.pending} />
        ))
    }

    let timeSlotsContent: React.ReactNode
    if (isNew) {
        timeSlotsContent = <MessageRow text={FORM_SECTION.saveCategoryFirstTimeSlots} />
    } else if (!timeSlotsEnabled) {
        timeSlotsContent = <MessageRow text={FORM_SECTION.offLine} />
    } else if (groupsQuery.isError) {
        timeSlotsContent = <MessageRow text={FORM_SECTION.groupsFailed} />
    } else if (!blocks) {
        timeSlotsContent = <PendingRow />
    } else if (blocks.length === 0) {
        timeSlotsContent = (
            <LinesRow
                lines={[FORM_SECTION.noGroupCategory]}
                linkTo={capacityPaths.timeSlots(accountSlug)}
                linkLabel={FORM_SECTION.goToTimeSlots}
            />
        )
    } else {
        timeSlotsContent = (
            <div className="capacity-section-blocks">
                {blocks.map((block) => (
                    <div className="capacity-section-block" key={block.label}>
                        <div className="capacity-section-block-label">{block.label}</div>
                        {block.groups.map((group) => (
                            <TimeSlotGroupCard
                                key={group.id}
                                accountSlug={accountSlug}
                                group={group}
                                detail={details[group.id]}
                            />
                        ))}
                    </div>
                ))}
            </div>
        )
    }

    return (
        <CapacitySectionShell
            capacities={capacityContent}
            timeSlots={
                <>
                    <Reveal open={warningOpen}>
                        <p className="capacity-section-warning" role="status">
                            {countUnknown ? FORM_SECTION.offWarningUnknown : FORM_SECTION.offWarning(losing ?? 0)}
                            {countUnknown && (
                                <span className="capacity-section-warning-note">{FORM_SECTION.offCountUnknown}</span>
                            )}
                        </p>
                    </Reveal>
                    {timeSlotsContent}
                </>
            }
            timeSlotsControl={timeSlotsControl}
        />
    )
}
